import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { Box, CircularProgress, Container, Grid, Typography } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';

import { Section, EditButton } from 'components/DeliveryManager';
import useSearchParams from 'hooks/useSearchParams';
import { DELIVERY_ADDRESS, ADDRESS_CORRECTED, ADDRESS_MISSING_UNIT } from 'constants/common';
import CorrectedValue from 'components/CorrectedValue';

const PREFIX = 'DeliveryAddress';

const classes = {
  text: `${PREFIX}-text`,
  error: `${PREFIX}-error`,
  notice: `${PREFIX}-notice`,
};

const COLORS = {
  GRAY: '#4a4a4a',
  LIGHT_GRAY: '#aaa',
  GREEN: '#3a6a4a',
  ORANGE: '#f5a623',
  RED: 'red',
};

const StyledSection = styled(Section)(({ theme }) => ({
  [`& .${classes.text}`]: {
    marginTop: 0,
    fontSize: '14px',
    color: theme.colorTokens.onSurfaceTextBody,

    '&__bold': {
      fontWeight: 600,
    },
  },

  [`& .${classes.error}`]: {
    fontSize: '13px',
    color: COLORS.RED,
    fontStyle: 'italic',
  },

  [`& .${classes.notice}`]: {
    fontSize: '13px',
    color: COLORS.ORANGE,
    fontStyle: 'italic',
  },
}));

const StrikethroughText = styled(Typography)({
  textDecoration: 'line-through',
  color: COLORS.LIGHT_GRAY,
  fontSize: '0.8em',
});

const DiffText = styled(Typography)(({ isDiff }) => ({
  color: isDiff ? COLORS.GREEN : COLORS.GRAY,
}));

const FlexContainer = styled('div')({
  display: 'flex',
});

const showDiff = ({ str, orig }) => {
  const isDiff = (str && !orig) || (orig && !str) || (str && orig && str.toUpperCase() !== orig.toUpperCase());
  return (
    <>
      {isDiff && <StrikethroughText>{orig}</StrikethroughText>}
      <DiffText isDiff={isDiff}>{isDiff ? str : orig}</DiffText>
    </>
  );
};

const showDiffZip = (addressComponents, orig) => {
  if (!addressComponents?.componentNotes?.zip && !addressComponents?.componentNotes?.zip4) {
    return <DiffText isDiff={false}>{orig}</DiffText>;
  }

  let displayZip = addressComponents.zip;
  if (orig.includes('-') && !!addressComponents?.zip4) {
    displayZip = addressComponents.fullZip;
  }
  return (
    <>
      <StrikethroughText>{orig}</StrikethroughText>
      <DiffText isDiff={true}>{displayZip}</DiffText>
    </>
  );
};

const CorrectedAddressView = memo(({ address, addressComponents, classes, onConfirm }) => {
  const street1 = showDiff({
    str: !!addressComponents?.componentNotes?.street1 ? addressComponents?.streetLine : address?.street,
    orig: address?.street,
  });
  const street2 = showDiff({
    str:
      !!addressComponents?.componentNotes?.street2 || !!addressComponents?.componentNotes?.secondaryAddressUnitDesignator
        ? addressComponents?.secondaryAddressLine
        : address?.street2,
    orig: address?.street2,
  });
  const city = showDiff({
    str: !!addressComponents?.componentNotes?.city ? addressComponents?.city : address?.city,
    orig: address?.city,
  });
  const state = showDiff({
    str: !!addressComponents?.componentNotes?.state ? addressComponents?.state : address?.state,
    orig: address?.state,
  });
  const zipcode = showDiffZip(addressComponents, address?.zipcode);

  return (
    <div>
      <p className={classes.text}>{street1 || 'N/A'}</p>
      <p className={clsx(classes.text, `${classes.text}__bold`)}>{street2 || 'N/A'}</p>
      <FlexContainer>
        {city}, {state}, {zipcode}
      </FlexContainer>
      <div>
        <p>
          <span className={classes.notice}>It appears your address may not be correct, please confirm the suggested address</span>
        </p>
      </div>
      <div>
        <EditButton size="small" variant="outlined" color="primary" onClick={onConfirm}>
          Confirm
        </EditButton>
      </div>
    </div>
  );
});

const MissingUnitAddressView = memo(({ address, correctedInfo, classes }) => {
  return (
    <div>
      <p className={classes.text}>{address?.street || 'N/A'}</p>
      <Box mb={0.5} data-testid="address-street2">
        <CorrectedValue
          originValue={address?.street2 || 'N/A'}
          correctedValue={correctedInfo?.street2 || 'N/A'}
          originClassName={clsx(classes.text, `${classes.text}__bold`)}
        />
      </Box>
      <p className={classes.text}>
        {address?.city}, {address?.state}, {address?.zipcode}
      </p>
      <div>
        <p>
          <span className={classes.notice}>
            {address?.street2
              ? 'There appears to be an issue with your unit number, we may have an invalid one on file. Please update it.'
              : 'We seem to be missing a unit number for your address. Please update it'}
          </span>
        </p>
      </div>
    </div>
  );
});

const StandardAddressView = memo(({ address, correctedInfo, classes, failed }) => {
  return (
    <div>
      <Box mb={0.5} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box>
          <Box data-testid="address-street">
            <CorrectedValue originValue={address?.street || 'N/A'} correctedValue={correctedInfo?.street || 'N/A'} originClassName={classes.text} />
          </Box>
          <Box data-testid="address-street2">
            <CorrectedValue
              originValue={address?.street2 || 'N/A'}
              correctedValue={correctedInfo?.street2 || 'N/A'}
              originClassName={clsx(classes.text, `${classes.text}__bold`)}
            />
          </Box>
        </Box>
      </Box>
      <CorrectedValue
        originValue={`${address?.city} ${address?.state} ${address?.zipcode}`}
        correctedValue={`${correctedInfo?.city} ${correctedInfo?.state} ${correctedInfo?.zipcode}`}
        originClassName={classes.text}
      />
      {failed && (
        <div>
          <p>
            <span className={classes.error}>It appears your address may not be correct</span>
          </p>
        </div>
      )}
    </div>
  );
});

export const DeliveryAddress = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { deliveryStore } = store;
  const { delivery, loadingDelivery } = deliveryStore;

  const address = delivery?.shipment?.dropoff_address;
  const failed = delivery?.enriched_address?.deliverableAddressStatus === 'FAILURE';
  const corrected = delivery?.enriched_address?.corrected === true;
  const addressComponents = delivery?.enriched_address?.enrichedAddress?.addressComponents;
  const incident = delivery?.incident;
  const isMissingUnit = incident?.type === 'ADDRESS-MISSING-UNIT';
  const correctedInfo = delivery?.customer_profile?.corrected_address || address;

  const handleOpenDialog = useCallback(
    (type) => {
      searchParams.delete('dialog');
      deliveryStore.updateField('waitingWS', false);

      switch (type) {
        case 'confirm':
          searchParams.set('reason', ADDRESS_CORRECTED);
          break;
        case 'street2':
          deliveryStore.updateField('editField', 'street2');
          searchParams.set('reason', ADDRESS_MISSING_UNIT);
          break;
        case 'address':
        default:
          deliveryStore.updateField('editField', 'address');
          searchParams.set('reason', DELIVERY_ADDRESS);
          break;
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams, deliveryStore],
  );

  const handleEditAddress = useCallback(() => {
    handleOpenDialog(isMissingUnit ? 'street2' : 'address');
  }, [handleOpenDialog, isMissingUnit]);

  const handleConfirm = useCallback(() => {
    handleOpenDialog('confirm');
  }, [handleOpenDialog]);

  const addressContent = useMemo(() => {
    if (corrected) {
      return (
        <CorrectedAddressView
          address={address}
          addressComponents={addressComponents}
          classes={classes}
          onConfirm={handleConfirm}
        />
      );
    }

    if (isMissingUnit) {
      return <MissingUnitAddressView address={address} correctedInfo={correctedInfo} classes={classes} />;
    }

    return <StandardAddressView address={address} correctedInfo={correctedInfo} classes={classes} failed={failed} />;
  }, [corrected, isMissingUnit, address, addressComponents, correctedInfo, failed, handleConfirm]);

  if (loadingDelivery) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
          <CircularProgress color="primary" />
        </Grid>
      </Container>
    );
  }

  return (
    <StyledSection>
      <Box className="section__header" mb={2}>
        <span className="section__title">Delivery Address:</span>
        {!failed && !corrected && (
          <EditButton size="small" variant="outlined" color="primary" onClick={handleEditAddress}>
            Edit Address
          </EditButton>
        )}
      </Box>
      <div className="section__body">
        {addressContent}
        {failed && !isMissingUnit && (
          <span className={classes.error}>
            Please contact <a href="mailto:support@gojitsu.com">support</a> to update your address.
          </span>
        )}
      </div>
    </StyledSection>
  );
});
